import { useTranslationWithPrefix } from '@hotjar/translations';
import { Text } from '@hotjar/hotsauce';
import { useCallback } from 'react';
import { AdvancedSelect } from '../../../../common/AdvancedSelectA11y/AdvancedSelect';
import { IconOption } from '../../../../common/AdvancedSelectA11y/IconOption';
import type { SurveyQuestion } from '../../../../../domain/Surveys/Questions/SurveyQuestion';
import { getQuestionLabelAndIcon } from '../../../utils';
import type { SkippedNextQuestionOption, NextQuestionOption } from './common';
import { getOption, useQuestionLabel } from './common';

const TRANSLATION_PREFIX = 'feedback:wizard.section.questions.next-question';

export const SkippedQuestionSelector = ({
  question,
  questions,
  onSkippedChange,
}: {
  question: SurveyQuestion;
  questions: SurveyQuestion[];
  onSkippedChange: (option: SkippedNextQuestionOption) => void;
}): JSX.Element => {
  const { t } = useTranslationWithPrefix(TRANSLATION_PREFIX);
  const getQuestionLabel = useQuestionLabel();
  const skippedNextQuestion: NextQuestionOption =
    question.nextIfSkipped !== undefined && question.nextIfSkipped !== null
      ? getOption(questions, question.nextIfSkipped)
      : { type: 'by-order' };

  const onSelect = useCallback(
    (value: 'by-order' | 'thank-you' | 'based-on' | string) => {
      if (value === 'by-order') {
        onSkippedChange({ type: 'by-order' });
        return;
      }
      if (value === 'thank-you') {
        onSkippedChange({ type: 'thank-you' });
        return;
      }

      const surveyQuestion = questions.find((q) => q.uuid === value);
      if (surveyQuestion === undefined) return;
      onSkippedChange({ type: 'survey-question', question: surveyQuestion });
    },
    [onSkippedChange, questions],
  );

  return (
    <>
      <Text textType="subtitle2">{t('skipped.text')}</Text>
      <AdvancedSelect
        id="skipped-question-selector"
        ariaLabel={t('skipped.label')}
        selectedValue={
          skippedNextQuestion.type === 'survey-question'
            ? skippedNextQuestion.question.uuid
            : skippedNextQuestion.type
        }
        onSelect={onSelect}
        maxWidth={350}
      >
        {[
          <IconOption value="by-order" key="byOrder" id="byOrder">
            {getQuestionLabel(questions, { type: 'by-order' })}
          </IconOption>,
          <IconOption value="thank-you" key="thankYou" id="thankYou">
            {getQuestionLabel(questions, { type: 'thank-you' })}
          </IconOption>,
          ...questions
            .filter((q) => q.uuid !== question.uuid)
            .map((surveyQuestion) => (
              <IconOption
                key={surveyQuestion.uuid}
                id={surveyQuestion.uuid}
                icon={getQuestionLabelAndIcon(surveyQuestion.type).icon}
                value={surveyQuestion.uuid}
              >
                {getQuestionLabel(questions, { type: 'survey-question', question: surveyQuestion })}
              </IconOption>
            )),
        ]}
      </AdvancedSelect>
    </>
  );
};
